/**
 * Event Pricing System - Complete Library
 *
 * This file contains both configuration and core business logic.
 * Edit the EventPricingConfig object below to customize settings.
 *
 * Architecture: Maintainability-first design
 * - Configuration at top of file (easy to find and edit)
 * - Named functions with single responsibility
 * - Comprehensive error handling
 * - Detailed logging for debugging
 *
 * @see MAINTAINABLE_ARCHITECTURE.md for architecture details
 * @see THEME_STRUCTURE_ANALYSIS.md for DOM structure
 * @see EVENT_PRICING_SAFEGUARDS.md for safety mechanisms
 */

// ============================================================================
// CONFIGURATION SECTION
// ============================================================================
// Edit this section to customize behavior without touching core logic below

window.EventPricingConfig = {

  // API Configuration
  api: {
    base: 'https://apps.freedomenduro.cn',
    endpoints: {
      validateToken: '/event/validate-token',
      getItems: '/event/items'  // Renamed from getPrices - returns both price and inventory
    },
    timeout: 10000
  },

  // Cookie Configuration
  cookies: {
    tokenName: 'event_token',
    slugName: 'event_slug',
    path: '/',
    sameSite: 'Lax'
  },

  // DOM Selectors
  // {id} will be replaced with product.id
  // {key} will be replaced with line_item.key
  selectors: {
    productDetail: {
      container: '.product-price-container',
      activitySection: '#activity-price-{id}',
      normalSection: '#normal-price-{id}',
      badge: '#activity-badge-{id}',
      priceValue: '#activity-price-value-{id}',
      sellingPrice: '#activity-selling-price-{id}',
      originalPrice: '#activity-original-price-{id}',
      referencePrices: '#activity-reference-prices-{id}'
    },
    productLoop: {
      productCard: '.product[data-product-id]',
      activitySection: '.activity-price-section',
      normalSection: '.normal-price-section',
      priceValue: '.activity-price-value',
      sellingPrice: '.activity-selling-price',
      originalPrice: '.activity-original-price'
    },
    cartPage: {
      itemRow: '[data-product-id="{key}"]',
      activityPrice: '#activity-price-cart-{key}',
      normalPrice: '#normal-price-cart-{key}',
      priceValue: '#activity-price-value-cart-{key}',
      sellingPrice: '#activity-selling-price-cart-{key}',
      compareLabel: '#activity-compare-label-cart-{key}',
      originalPrice: '#activity-original-price-cart-{key}',
      badge: '#activity-badge-cart-{key}',
      activityTotalSection: '#activity-total-section-cart-{key}',
      activityTotal: '#activity-total-cart-{key}',
      activityTotalCompare: '#activity-total-compare-cart-{key}',
      normalTotal: '#normal-total-cart-{key}',
      totalTop: '#total-cart-top',
      totalBottom: '#total-cart-bottom',
      // Cart savings row
      savingsRow: '#activity-cart-savings-row',
      savings: '#activity-cart-savings'
    },
    cartDropdown: {
      container: '#cart-dropdown',
      itemPrice: '[data-item-key="{key}"]',
      activityPrice: '#activity-price-dropdown-{key}',
      normalPrice: '#normal-price-dropdown-{key}',
      priceValue: '#activity-price-value-dropdown-{key}',
      sellingPrice: '#activity-selling-price-dropdown-{key}',
      originalPrice: '#activity-original-price-dropdown-{key}',
      badge: '#activity-badge-dropdown-{key}',
      activityTotal: '#activity-total-dropdown-{key}',
      normalTotal: '#normal-total-dropdown-{key}'
    }
  },

  // Text Templates
  text: {
    badgeTemplate: '{eventName}专享价',
    defaultEventName: '活动',
    pickupBadgeText: '现场可取',
    pickupDeliveryText: '下单付款后到指定地点领取'
  },

  // Inventory Badge Configuration (centralized for maintainability)
  badges: {
    // Priority 1: API pickup_available = true
    pickup: {
      text: '现场可取',
      background: '#e32e00',
      color: '#fff',
      className: 'badge-event-swatch'
    },
    // Priority 2: Product tag "是否现货_国内现货"
    domestic: {
      text: '国内现货',
      tag: '是否现货_国内现货',
      background: '#F07B67',
      color: '#fff',
      className: 'badge-domestic-swatch'
    },
    // Common styles
    common: {
      padding: '4px 8px',
      fontSize: '9px',
      fontWeight: 'bold',
      borderRadius: '0',
      position: 'absolute',
      bottom: '0',
      right: '0',
      zIndex: '10'
    }
  },

  // Pickup Badge Selectors (for inventory display)
  pickupSelectors: {
    productDetail: {
      inventoryContainer: '.variant-inventory-info',
      locationInfo: '.location-info',
      deliveryInfo: '.delivery-info'
    },
    productLoop: {
      inventoryBadge: '.inventory-badge'
    }
  },

  // Feature Flags
  features: {
    enableLogging: true,
    logLevel: 'info',
    batchRequests: true,
    showLoadingState: false,
    autoUpdateCart: true,
    observeCartDropdown: true
  },

  // Price Formatting
  formatting: {
    useShopifyFormat: true,
    fallbackFormat: '¥{{amount}}',
    removeTrailingZeros: false
  },

  // Helper Methods
  getSelector: function(selectorTemplate, value) {
    return selectorTemplate.replace(/\{(id|key)\}/g, value);
  },

  getApiUrl: function(endpoint) {
    return this.api.base + this.api.endpoints[endpoint];
  },

  getBadgeText: function(eventName) {
    if (window.EventData && window.EventData.price_label) {
      return window.EventData.price_label;
    }
    return this.text.badgeTemplate.replace('{eventName}', eventName || this.text.defaultEventName);
  },

  validate: function() {
    var errors = [];

    if (!this.api.base) {
      errors.push('api.base is required');
    }

    if (!this.selectors.productDetail) {
      errors.push('selectors.productDetail is required');
    }

    if (errors.length > 0) {
      console.error('[EventPricingConfig] Validation errors:', errors);
      return false;
    }

    console.log('[EventPricingConfig] ✓ Configuration valid');
    return true;
  }
};

// Auto-validate on load (only if logging enabled)
if (window.EventPricingConfig.features.enableLogging) {
  window.EventPricingConfig.validate();
}

// ============================================================================
// CORE LIBRARY - BUSINESS LOGIC
// ============================================================================
// Do not edit below unless you need to modify core functionality

(function() {
  'use strict';

  // Get configuration
  var config = window.EventPricingConfig;

  if (!config) {
    console.error('[EventPricing] Configuration not loaded!');
    return;
  }

  // ============================================================================
  // Logging Utilities
  // ============================================================================

  /**
   * Log info message (only if logging enabled)
   */
  function log(message, data) {
    if (!config.features.enableLogging) return;
    if (config.features.logLevel === 'error' || config.features.logLevel === 'warn') return;

    console.log('[EventPricing]', message, data || '');
  }

  /**
   * Log warning message
   */
  function logWarn(message, data) {
    if (!config.features.enableLogging) return;
    if (config.features.logLevel === 'error') return;

    console.warn('[EventPricing]', message, data || '');
  }

  /**
   * Log error message (always logged)
   */
  function logError(message, error) {
    console.error('[EventPricing]', message, error || '');
  }

  /**
   * Log verbose debug message
   */
  function logVerbose(message, data) {
    if (!config.features.enableLogging) return;
    if (config.features.logLevel !== 'verbose') return;

    console.log('[EventPricing] [VERBOSE]', message, data || '');
  }

  // ============================================================================
  // Cookie Management
  // ============================================================================

  /**
   * Get cookie value by name
   *
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  function getCookie(name) {
    var value = '; ' + document.cookie;
    var parts = value.split('; ' + name + '=');

    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }

    return null;
  }

  /**
   * Set cookie with expiration date
   *
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {Date} expiresDate - Expiration date
   */
  function setCookie(name, value, expiresDate) {
    var expires = '';

    if (expiresDate) {
      expires = '; expires=' + expiresDate.toUTCString();
    }

    var cookieString = name + '=' + (value || '') + expires +
                       '; path=' + config.cookies.path +
                       '; SameSite=' + config.cookies.sameSite;

    document.cookie = cookieString;

    logVerbose('Cookie set: ' + name, value);
  }

  /**
   * Get event token from cookie
   *
   * @returns {string|null} Event token or null
   */
  function getEventToken() {
    return getCookie(config.cookies.tokenName);
  }

  /**
   * Get event slug from cookie
   *
   * @returns {string|null} Event slug or null
   */
  function getEventSlug() {
    return getCookie(config.cookies.slugName);
  }

  /**
   * Check if event token exists (system activation check)
   *
   * @returns {boolean} True if both token and slug exist
   */
  function hasEventToken() {
    return !!(getEventToken() && getEventSlug());
  }

  // ============================================================================
  // Cache Management
  // ============================================================================

  // ============================================================================
  // API Calls
  // ============================================================================

  /**
   * Validate event token and get event info
   *
   * @param {string} token - Access token to validate
   * @returns {Promise<object|null>} Response object with success, valid, event, message
   *
   * @example
   * var response = await validateToken('abc123');
   * if (response.success && response.valid) {
   *   console.log(response.event.name); // "KTM 2026"
   * }
   */
  async function validateToken(token) {
    if (!token) {
      logVerbose('No token provided to validate');
      return { success: false, valid: false, message: '链接已失效，请联系客服获取新的活动链接' };
    }

    try {
      var url = config.getApiUrl('validateToken');
      logVerbose('Validating token at: ' + url);

      var response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token })
      });

      if (!response.ok) {
        logWarn('Token validation failed with status: ' + response.status);
        return { success: false, valid: false, message: '网络连接异常，请检查网络设置后刷新页面', isNetworkError: true };
      }

      var data = await response.json();

      if (data.success && data.valid && data.event) {
        log('Token validated successfully', data.event.name);
        return { success: true, valid: true, data: data.event };
      }

      log('Token invalid or expired');
      return { success: false, valid: false, message: data.message || '链接已失效，请联系客服获取新的活动链接' };

    } catch (error) {
      // SAFEGUARD: Silent failure - don't break the page
      logWarn('Token validation error (this is OK for non-event pages)', error.message);
      return { success: false, valid: false, message: '网络连接异常，请检查网络设置后刷新页面', isNetworkError: true };
    }
  }

  /**
   * Get event items (price + inventory) for variants
   *
   * @param {string} slug - Event slug
   * @param {string} token - Access token
   * @param {object} variants - {variant_id: sku} mapping
   * @returns {Promise<object>} Response object with event_name and items
   *
   * @example
   * var result = await getItems('ktm-2026', 'token123', {123: 'SKU-A', 456: 'SKU-B'});
   * // Returns: {
   * //   event_name: "KTM 2026",
   * //   event_slug: "ktm-2026",
   * //   items: { "123": { event_price: 1599, pickup_available: true }, "456": { event_price: null, pickup_available: false } }
   * // }
   */
  async function getItems(slug, token, variants) {
    // SAFEGUARD: Validate inputs
    if (!slug || !token || !variants || Object.keys(variants).length === 0) {
      logVerbose('getItems called with invalid params');
      return { event_name: null, event_slug: null, items: {} };
    }

    try {
      var url = config.getApiUrl('getItems');
      logVerbose('Fetching items for ' + Object.keys(variants).length + ' variants');

      var response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_slug: slug,
          access_token: token,
          variants: variants
        })
      });

      if (!response.ok) {
        logWarn('Items fetch failed with status: ' + response.status);
        return { event_name: null, event_slug: null, items: {} };
      }

      var data = await response.json();

      if (data.success && data.data) {
        // Store event name globally for badge display
        if (data.data.event_name) {
          window.EventData = window.EventData || {};
          window.EventData.name = data.data.event_name;
          window.EventData.slug = data.data.event_slug;
          window.EventData.price_label = data.data.price_label || null;
        }

        var itemCount = data.data.items ? Object.keys(data.data.items).length : 0;
        log('Fetched ' + itemCount + ' event items for ' + (data.data.event_name || 'event'));

        return {
          event_name: data.data.event_name,
          event_slug: data.data.event_slug,
          items: data.data.items || {}
        };
      }

      logVerbose('No item data in response');
      return { event_name: null, event_slug: null, items: {} };

    } catch (error) {
      // SAFEGUARD: Silent failure
      logWarn('Items fetch error (this is OK)', error.message);
      return { event_name: null, event_slug: null, items: {} };
    }
  }

  // ============================================================================
  // Price Formatting
  // ============================================================================

  /**
   * Format price in yuan to display string
   *
   * @param {number} yuan - Price in yuan
   * @returns {string} Formatted price string (e.g., "¥599.00")
   */
  function formatPrice(yuan) {
    var num = parseFloat(yuan) || 0;
    var formatted = num.toFixed(2);

    if (config.formatting.removeTrailingZeros) {
      formatted = formatted.replace(/\.00$/, '');
    }

    return '¥' + formatted;
  }

  // ============================================================================
  // DOM Manipulation - Product Detail Page
  // ============================================================================

  /**
   * Get product price DOM elements
   *
   * @param {number} productId - Product ID
   * @returns {object|null} Object with element references or null
   */
  function getProductPriceElements(productId) {
    var selectors = config.selectors.productDetail;

    var elements = {
      activitySection: document.getElementById(config.getSelector(selectors.activitySection, productId).replace('#', '')),
      normalSection: document.getElementById(config.getSelector(selectors.normalSection, productId).replace('#', '')),
      badge: document.getElementById(config.getSelector(selectors.badge, productId).replace('#', '')),
      priceValue: document.getElementById(config.getSelector(selectors.priceValue, productId).replace('#', '')),
      sellingPrice: document.getElementById(config.getSelector(selectors.sellingPrice, productId).replace('#', '')),
      originalPrice: document.getElementById(config.getSelector(selectors.originalPrice, productId).replace('#', '')),
      referencePrices: document.getElementById(config.getSelector(selectors.referencePrices, productId).replace('#', ''))
    };

    // Check if required elements exist
    if (!elements.activitySection || !elements.normalSection) {
      logVerbose('Product price elements not found for product ' + productId);
      return null;
    }

    return elements;
  }

  // Cache for PDP variant prices (fetched once for all variants)
  var pdpPriceCache = null;

  /**
   * Update pickup badges on swatch options based on variant pickup_available
   */
  function updateSwatchPickupBadges() {
    if (!pdpPriceCache) return;

    var pickupBadgeConfig = config.badges.pickup;
    var updatedCount = 0;

    // Find all swatch badges with variant ID
    var swatchBadges = document.querySelectorAll('.badge-inventory[data-variant-id]');
    swatchBadges.forEach(function(badge) {
      var variantId = badge.getAttribute('data-variant-id');
      var priceData = pdpPriceCache[variantId];

      if (priceData && priceData.pickup_available) {
        // Update badge to show pickup available - keep original class structure
        badge.textContent = pickupBadgeConfig.text;
        badge.className = 'badge-swatch ' + pickupBadgeConfig.className + ' badge-inventory';
        badge.setAttribute('data-variant-id', variantId);
        updatedCount++;
      }
    });

    // Also check pickup-badge-placeholder elements (for variants without inventory badge)
    var placeholders = document.querySelectorAll('.pickup-badge-placeholder[data-variant-id]');
    placeholders.forEach(function(placeholder) {
      var variantId = placeholder.getAttribute('data-variant-id');
      var priceData = pdpPriceCache[variantId];

      if (priceData && priceData.pickup_available) {
        // Check if there's already a badge-inventory sibling
        var existingBadge = placeholder.parentElement.querySelector('.badge-inventory[data-variant-id="' + variantId + '"]');
        if (!existingBadge) {
          // Create new pickup badge using the same class as other badges
          var newBadge = document.createElement('span');
          newBadge.className = 'badge-swatch ' + pickupBadgeConfig.className;
          newBadge.textContent = pickupBadgeConfig.text;
          placeholder.parentElement.insertBefore(newBadge, placeholder.nextSibling);
          updatedCount++;
        }
      }
    });

    log('Updated ' + updatedCount + ' swatch pickup badges');
  }

  /**
   * Prefetch all variant prices for PDP
   * Called once on page load with all variants
   */
  async function prefetchAllVariantPrices(productJson) {
    if (!productJson || !productJson.variants || productJson.variants.length === 0) {
      logVerbose('No variants to prefetch');
      return null;
    }

    if (!hasEventToken()) {
      logVerbose('No event token - skipping prefetch');
      return null;
    }

    var slug = getEventSlug();
    var token = getEventToken();

    // Build variants map: {variant_id: sku}
    var variants = {};
    productJson.variants.forEach(function(v) {
      variants[v.id] = v.sku || '';
    });

    log('Prefetching prices for ' + Object.keys(variants).length + ' variants');
    var result = await getItems(slug, token, variants);
    pdpPriceCache = result.items || {};
    log('Prefetched prices cached:', Object.keys(pdpPriceCache).length + ' variants');

    // Update pickup badges on each swatch/variant option
    updateSwatchPickupBadges();

    return pdpPriceCache;
  }

  /**
   * Update product detail page price with event pricing
   *
   * @param {number} variantId - Shopify variant ID
   * @param {number} productId - Shopify product ID
   * @param {number} originalPriceYuan - Original price in yuan
   * @param {string} eventName - Event name for badge
   * @param {string} sku - Variant SKU (for pickup_available lookup)
   *
   * Dependencies:
   * - Requires event_token cookie
   * - Calls /event/prices API
   * - Updates DOM elements from config.selectors.productDetail
   *
   * Error handling:
   * - Returns early if no event token
   * - Returns early if API fails
   * - Returns early if elements not found
   * - Never throws errors
   *
   * @see THEME_STRUCTURE_ANALYSIS.md for DOM structure
   */
  /**
   * Reset product detail price to normal display
   */
  function resetProductDetailPrice(productId) {
    var activitySection = document.getElementById('activity-price-' + productId);
    var normalSection = document.getElementById('normal-price-' + productId);
    if (activitySection) activitySection.style.display = 'none';
    if (normalSection) normalSection.style.display = 'block';
    // Restore inventory info visibility when resetting to normal price
    var selectors = config.pickupSelectors.productDetail;
    var inventoryContainer = document.querySelector(selectors.inventoryContainer);
    if (inventoryContainer) inventoryContainer.style.display = '';
  }

  async function updateProductDetailPrice(variantId, productId, originalPriceYuan, eventName, sku) {
    logVerbose('updateProductDetailPrice called', { variantId: variantId, productId: productId, sku: sku });

    // Guard: Check prerequisites
    if (!hasEventToken()) {
      logVerbose('No event token - skipping');
      return;
    }

    // Step 1: Get price data from cache or fetch
    var priceData = null;
    if (pdpPriceCache && pdpPriceCache[variantId.toString()]) {
      // Use cached data
      priceData = pdpPriceCache[variantId.toString()];
      logVerbose('Using cached price data for variant ' + variantId);
    } else {
      // Fallback: fetch single variant if not in cache
      var slug = getEventSlug();
      var token = getEventToken();
      var variants = {};
      variants[variantId] = sku || '';
      var result = await getItems(slug, token, variants);
      priceData = result.items[variantId.toString()];
    }

    if (!priceData) {
      logVerbose('No data for variant ' + variantId);
      resetProductDetailPrice(productId);
      return;
    }

    // Check if has event price
    var eventPriceYuan = priceData.event_price;
    if (eventPriceYuan == null) {
      logVerbose('No event price for variant ' + variantId);
      resetProductDetailPrice(productId);
      return;
    }

    var eventNameText = eventName || config.text.defaultEventName;

    // SAFEGUARD: Only show if event price is lower
    if (eventPriceYuan >= originalPriceYuan) {
      logVerbose('Event price not lower than original, skipping');
      resetProductDetailPrice(productId);
      return;
    }

    // Step 2: Get DOM elements
    var elements = getProductPriceElements(productId);
    if (!elements) {
      logWarn('Could not find price elements for product ' + productId);
      return;
    }

    // Step 3: Update badge text
    if (elements.badge) {
      elements.badge.textContent = config.getBadgeText(eventNameText);
    }

    // Step 4: Update prices
    // Get compare_at_price for reference
    var priceContainer = document.querySelector('.product-price-container[data-product-id="' + productId + '"]');
    var compareAtPriceCents = priceContainer ? parseFloat(priceContainer.getAttribute('data-compare-at-price')) || 0 : 0;
    var compareAtPriceYuan = compareAtPriceCents / 100;

    // Event price (活动专享价)
    if (elements.priceValue) {
      elements.priceValue.textContent = formatPrice(eventPriceYuan);
    }

    // Selling price (日常价) - originalPriceYuan is the selling price
    if (elements.sellingPrice) {
      elements.sellingPrice.textContent = formatPrice(originalPriceYuan);
    }

    // Compare price (原价) - only show if different from selling price
    if (elements.originalPrice) {
      if (compareAtPriceYuan > originalPriceYuan) {
        elements.originalPrice.textContent = formatPrice(compareAtPriceYuan);
        elements.originalPrice.style.display = 'inline';
        // Show "原价" label
        if (elements.originalPrice.previousElementSibling) {
          elements.originalPrice.previousElementSibling.style.display = 'inline';
        }
      } else {
        // Hide compare price if same as selling price
        elements.originalPrice.style.display = 'none';
        if (elements.originalPrice.previousElementSibling) {
          elements.originalPrice.previousElementSibling.style.display = 'none';
        }
      }
    }

    // Step 5: Toggle visibility
    elements.activitySection.style.display = 'block';
    elements.normalSection.style.display = 'none';

    // Step 6: Toggle inventory info based on pickup_available
    var selectors = config.pickupSelectors.productDetail;
    var inventoryContainer = document.querySelector(selectors.inventoryContainer);
    if (inventoryContainer) {
      inventoryContainer.style.display = priceData.pickup_available ? 'none' : '';
    }

    log('Updated product detail price for variant ' + variantId);
  }

  // ============================================================================
  // DOM Manipulation - Product Loop/Grid
  // ============================================================================

  /**
   * Update product grid/loop prices (collection pages)
   *
   * Batch processes all products on page
   *
   * @param {string} eventName - Event name for badge
   */
  async function updateProductGridPrices(eventName) {
    logVerbose('updateProductGridPrices called');

    // Guard: Check prerequisites
    if (!hasEventToken()) {
      logVerbose('No event token - skipping grid update');
      return;
    }

    var productElements = document.querySelectorAll(config.selectors.productLoop.productCard);

    if (productElements.length === 0) {
      logVerbose('No product cards found on page');
      return;
    }

    log('Found ' + productElements.length + ' products in grid');

    // Build variants map: {variant_id: sku}
    // Include ALL variants from each product for pickup_available check
    var variants = {};
    var productData = [];

    productElements.forEach(function(element) {
      var variantId = parseInt(element.getAttribute('data-variant-id'));
      var originalPrice = parseFloat(element.getAttribute('data-original-price'));
      var comparePrice = parseFloat(element.getAttribute('data-compare-price')) || 0;
      var sku = element.getAttribute('data-sku') || '';

      // Get all variants for this product (for pickup_available check)
      var allVariants = [];
      var allVariantsAttr = element.getAttribute('data-all-variants');
      if (allVariantsAttr) {
        try {
          allVariants = JSON.parse(allVariantsAttr);
          // Add all variants to the request map
          allVariants.forEach(function(v) {
            variants[v.id] = v.sku || '';
          });
        } catch (e) {
          logWarn('Failed to parse all-variants data', e);
        }
      }

      if (variantId && originalPrice) {
        // Fallback: add primary variant if not in allVariants
        if (!variants[variantId]) {
          variants[variantId] = sku;
        }
        productData.push({
          element: element,
          variantId: variantId,
          originalPrice: originalPrice,
          comparePrice: comparePrice,
          sku: sku,
          allVariantIds: allVariants.map(function(v) { return v.id; })
        });
      }
    });

    if (Object.keys(variants).length === 0) {
      logVerbose('No valid variants found');
      return;
    }

    // Fetch all items (price + inventory) in one API call
    var slug = getEventSlug();
    var token = getEventToken();
    var result = await getItems(slug, token, variants);

    var updatedCount = 0;

    // Update each product
    productData.forEach(function(product) {
      var priceData = result.items[product.variantId.toString()];

      // Check pickup_available for ANY variant of this product
      var hasPickupAvailable = false;
      if (product.allVariantIds && product.allVariantIds.length > 0) {
        product.allVariantIds.forEach(function(vid) {
          var vData = result.items[vid.toString()];
          if (vData && vData.pickup_available) {
            hasPickupAvailable = true;
          }
        });
      } else if (priceData && priceData.pickup_available) {
        hasPickupAvailable = true;
      }

      // Update pickup badge if any variant has pickup_available
      if (hasPickupAvailable) {
        updateProductGridPickupBadge(product.element, true);
      }

      // Hide products in pickup tab that don't have pickup_available
      var isInPickupTab = product.element.closest('#tab-pickup') !== null;
      if (isInPickupTab && !hasPickupAvailable) {
        product.element.style.display = 'none';
        return;
      }

      // Find the lowest event price across all variants
      var eventPriceYuan = null;
      if (product.allVariantIds) {
        for (var vi = 0; vi < product.allVariantIds.length; vi++) {
          var vData = result.items[product.allVariantIds[vi].toString()];
          if (vData && vData.event_price != null) {
            if (eventPriceYuan == null || vData.event_price < eventPriceYuan) {
              priceData = vData;
              eventPriceYuan = vData.event_price;
            }
          }
        }
      }
      // Fallback to primary variant if allVariantIds was empty
      if (eventPriceYuan == null && priceData && priceData.event_price != null) {
        eventPriceYuan = priceData.event_price;
      }

      if (eventPriceYuan == null) {
        logVerbose('No event price for any variant of product ' + product.variantId);
        return;
      }

      // Debug logging
      logVerbose('Comparing prices for variant ' + product.variantId + ': eventPrice=' + eventPriceYuan + ', originalPrice=' + product.originalPrice);

      // SAFEGUARD: Only show if lower
      if (eventPriceYuan >= product.originalPrice) {
        logVerbose('Skipping variant ' + product.variantId + ': event price not lower');
        return;
      }

      var activitySection = product.element.querySelector(config.selectors.productLoop.activitySection);
      var normalSection = product.element.querySelector(config.selectors.productLoop.normalSection);

      if (!activitySection || !normalSection) return;

      var priceValue = activitySection.querySelector(config.selectors.productLoop.priceValue);
      var sellingPrice = activitySection.querySelector(config.selectors.productLoop.sellingPrice);
      var originalPriceEl = activitySection.querySelector(config.selectors.productLoop.originalPrice);
      var compareLabelEl = activitySection.querySelector('.activity-compare-label');
      var badgeEl = activitySection.querySelector('.activity-badge');

      // Update badge text with event name
      if (badgeEl) {
        var eventName = (window.EventData && window.EventData.name) ? window.EventData.name : config.text.defaultEventName;
        badgeEl.textContent = config.getBadgeText(eventName);
      }

      // Event price (活动专享价)
      if (priceValue) {
        priceValue.textContent = formatPrice(eventPriceYuan);
      }

      // Selling price (日常价)
      if (sellingPrice) {
        sellingPrice.textContent = formatPrice(product.originalPrice);
      }

      // Compare price (原价) - only show if different from selling price
      if (originalPriceEl) {
        if (product.comparePrice > product.originalPrice) {
          originalPriceEl.textContent = formatPrice(product.comparePrice);
          originalPriceEl.style.display = 'inline';
          if (compareLabelEl) compareLabelEl.style.display = 'inline';
        } else {
          originalPriceEl.style.display = 'none';
          if (compareLabelEl) compareLabelEl.style.display = 'none';
        }
      }

      activitySection.style.display = 'block';
      normalSection.style.display = 'none';

      updatedCount++;
    });

    log('Updated ' + updatedCount + ' product prices in grid');
  }

  // ============================================================================
  // DOM Manipulation - Cart
  // ============================================================================

  /**
   * Update cart prices (both cart page and dropdown)
   *
   * Fetches current cart via AJAX, then updates all prices
   */
  async function updateCartPrices() {
    logVerbose('updateCartPrices called');

    // Guard: Check prerequisites
    if (!hasEventToken()) {
      logVerbose('No event token - skipping cart update');
      return;
    }

    try {
      // Fetch current cart
      var cartResponse = await fetch('/cart.js');
      var cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) {
        logVerbose('Cart is empty');
        return;
      }

      log('Updating cart with ' + cart.items.length + ' items');

      var slug = getEventSlug();
      var token = getEventToken();

      // Build variants map: {variant_id: sku}
      var variants = {};
      cart.items.forEach(function(item) {
        variants[item.variant_id] = item.sku || '';
      });

      // Fetch event items (price + inventory)
      var result = await getItems(slug, token, variants);

      // If no items returned, skip update
      if (!result.items || Object.keys(result.items).length === 0) {
        logVerbose('No event items for cart items');
        return;
      }

      var prices = result.items;

      var totalEventPrice = 0;
      var totalSellingPrice = 0;
      var totalComparePrice = 0;
      var hasComparePrice = false;

      // Update each cart item
      cart.items.forEach(function(item) {
        var priceData = prices[item.variant_id.toString()];
        // event_price is in yuan, item.price is in cents - convert to yuan
        var eventPrice = priceData ? priceData.event_price : null;
        var sellingPriceYuan = item.price / 100;

        // Get compare_at_price from cart item's variant
        var compareAtPriceYuan = 0;
        var itemEl = document.getElementById('product-id-' + item.key);
        if (itemEl) {
          var compareAtPrice = parseFloat(itemEl.getAttribute('data-compare-at-price')) || 0;
          compareAtPriceYuan = compareAtPrice / 100;
        }

        // Calculate totals
        var priceYuan = (eventPrice != null) ? eventPrice : sellingPriceYuan;
        totalEventPrice += priceYuan * item.quantity;
        totalSellingPrice += sellingPriceYuan * item.quantity;

        if (compareAtPriceYuan > sellingPriceYuan) {
          totalComparePrice += compareAtPriceYuan * item.quantity;
          hasComparePrice = true;
        } else {
          totalComparePrice += sellingPriceYuan * item.quantity;
        }

        // Update item display (pass null if no event price)
        updateCartItemDisplay(item, eventPrice != null ? priceData : null);
      });

      // Update cart total with all price data
      updateCartTotal(totalEventPrice, totalSellingPrice, totalComparePrice, hasComparePrice);

      log('Cart prices updated successfully');

    } catch (error) {
      logWarn('Cart update failed', error.message);
    }
  }

  /**
   * Update single cart item display
   *
   * @param {object} item - Cart line item
   * @param {object|null} priceData - Event price data or null
   */
  function updateCartItemDisplay(item, priceData) {
    var itemKey = item.key;

    // Update cart page
    updateCartPageItem(itemKey, item, priceData);

    // Update cart dropdown
    updateCartDropdownItem(itemKey, item, priceData);
  }

  /**
   * Update cart page item price
   */
  function updateCartPageItem(itemKey, item, priceData) {
    var selectors = config.selectors.cartPage;
    var activityPriceEl = document.getElementById(config.getSelector(selectors.activityPrice, itemKey).replace('#', ''));

    if (!activityPriceEl) return;

    var normalPriceEl = document.getElementById(config.getSelector(selectors.normalPrice, itemKey).replace('#', ''));
    var activityTotalEl = document.getElementById(config.getSelector(selectors.activityTotal, itemKey).replace('#', ''));
    var normalTotalEl = document.getElementById(config.getSelector(selectors.normalTotal, itemKey).replace('#', ''));

    // If no event price, show normal price
    if (!priceData) {
      activityPriceEl.style.display = 'none';
      if (normalPriceEl) normalPriceEl.style.display = 'block';
      if (activityTotalEl) activityTotalEl.style.display = 'none';
      if (normalTotalEl) normalTotalEl.style.display = 'inline';
      return;
    }

    var eventPriceYuan = priceData.event_price;
    var eventName = (window.EventData && window.EventData.name) ? window.EventData.name : config.text.defaultEventName;

    // Get price data
    var itemEl = document.getElementById('product-id-' + itemKey);
    var compareAtPrice = itemEl ? parseFloat(itemEl.getAttribute('data-compare-at-price')) || 0 : 0;
    var compareAtPriceYuan = compareAtPrice / 100;
    var sellingPriceYuan = item.price / 100;

    // Update badge text
    var badgeEl = document.getElementById(config.getSelector(selectors.badge, itemKey).replace('#', ''));
    if (badgeEl) {
      badgeEl.textContent = config.getBadgeText(eventName);
    }

    // Update unit price display
    var priceValueEl = document.getElementById(config.getSelector(selectors.priceValue, itemKey).replace('#', ''));
    var sellingPriceEl = document.getElementById(config.getSelector(selectors.sellingPrice, itemKey).replace('#', ''));
    var compareLabelEl = document.getElementById(config.getSelector(selectors.compareLabel, itemKey).replace('#', ''));
    var originalPriceEl = document.getElementById(config.getSelector(selectors.originalPrice, itemKey).replace('#', ''));

    // Event price (专享价)
    if (priceValueEl) {
      priceValueEl.textContent = formatPrice(eventPriceYuan);
    }

    // Selling price (日常价)
    if (sellingPriceEl) {
      sellingPriceEl.textContent = formatPrice(sellingPriceYuan);
    }

    // Compare price (原价) - only show if different from selling price
    if (originalPriceEl && compareLabelEl) {
      if (compareAtPriceYuan > sellingPriceYuan) {
        originalPriceEl.textContent = formatPrice(compareAtPriceYuan);
        originalPriceEl.style.display = 'inline';
        compareLabelEl.style.display = 'inline';
      } else {
        originalPriceEl.style.display = 'none';
        compareLabelEl.style.display = 'none';
      }
    }

    // Update subtotal - event price + compare price (strikethrough)
    if (activityTotalEl) {
      activityTotalEl.textContent = formatPrice(eventPriceYuan * item.quantity);
      activityTotalEl.style.display = 'inline';
    }

    // Compare price subtotal (strikethrough, no label) - show 原价
    var activityTotalCompareEl = document.getElementById(config.getSelector(selectors.activityTotalCompare, itemKey).replace('#', ''));
    if (activityTotalCompareEl) {
      // Only show if compare price > selling price
      if (compareAtPriceYuan > sellingPriceYuan) {
        activityTotalCompareEl.textContent = formatPrice(compareAtPriceYuan * item.quantity);
        activityTotalCompareEl.style.display = 'inline';
      } else {
        activityTotalCompareEl.style.display = 'none';
      }
    }

    // Toggle visibility
    activityPriceEl.style.display = 'block';
    if (normalPriceEl) normalPriceEl.style.display = 'none';
    if (normalTotalEl) normalTotalEl.style.display = 'none';
  }

  /**
   * Update cart dropdown item price
   */
  function updateCartDropdownItem(itemKey, item, priceData) {
    var selectors = config.selectors.cartDropdown;
    var activityPriceEl = document.getElementById(config.getSelector(selectors.activityPrice, itemKey).replace('#', ''));

    if (!activityPriceEl) return;

    var normalPriceEl = document.getElementById(config.getSelector(selectors.normalPrice, itemKey).replace('#', ''));

    // If no event price, show normal price
    if (!priceData) {
      activityPriceEl.style.display = 'none';
      if (normalPriceEl) normalPriceEl.style.display = 'block';
      return;
    }

    var eventPriceYuan = priceData.event_price;
    var sellingPriceYuan = item.price / 100;
    var eventName = (window.EventData && window.EventData.name) ? window.EventData.name : config.text.defaultEventName;

    // Update badge text
    var badgeEl = document.getElementById(config.getSelector(selectors.badge, itemKey).replace('#', ''));
    if (badgeEl) {
      badgeEl.textContent = config.getBadgeText(eventName);
    }

    // Update event price display
    var priceValueEl = document.getElementById(config.getSelector(selectors.priceValue, itemKey).replace('#', ''));
    if (priceValueEl) {
      priceValueEl.textContent = formatPrice(eventPriceYuan);
    }

    // Update selling price display (日常价)
    var sellingPriceEl = document.getElementById(config.getSelector(selectors.sellingPrice, itemKey).replace('#', ''));
    if (sellingPriceEl) {
      sellingPriceEl.textContent = formatPrice(sellingPriceYuan);
    }

    // Update subtotal
    var activityTotalEl = document.getElementById(config.getSelector(selectors.activityTotal, itemKey).replace('#', ''));
    var normalTotalEl = document.getElementById(config.getSelector(selectors.normalTotal, itemKey).replace('#', ''));

    if (activityTotalEl) {
      activityTotalEl.textContent = formatPrice(eventPriceYuan * item.quantity);
      activityTotalEl.style.display = 'inline';
    }
    // Hide normal total when showing activity total
    if (normalTotalEl) {
      normalTotalEl.style.display = 'none';
    }

    activityPriceEl.style.display = 'block';
    if (normalPriceEl) normalPriceEl.style.display = 'none';
  }

  /**
   * Update cart total display
   */
  function updateCartTotal(totalEventYuan, totalSellingYuan, totalCompareYuan, hasComparePrice) {
    var topEl = document.getElementById('total-cart-top');
    var bottomEl = document.getElementById('total-cart-bottom');
    var headerEls = document.querySelectorAll('.cartTotalSelector');

    var formattedTotal = formatPrice(totalEventYuan);

    if (topEl) {
      topEl.textContent = formattedTotal;
    }
    if (bottomEl) {
      bottomEl.textContent = formattedTotal;
    }
    // Update header cart total
    headerEls.forEach(function(el) {
      el.textContent = formattedTotal;
    });

    // Update cart savings row
    var selectors = config.selectors.cartPage;
    var savingsRowEl = document.getElementById(selectors.savingsRow.replace('#', ''));
    var savingsEl = document.getElementById(selectors.savings.replace('#', ''));

    var savings = totalSellingYuan - totalEventYuan;

    log('Cart savings update:', { savings: savings });

    if (savingsRowEl && savingsEl) {
      if (savings > 0) {
        savingsEl.textContent = formatPrice(savings);
        savingsRowEl.style.display = 'table-row';
      } else {
        savingsRowEl.style.display = 'none';
      }
    }
  }

  // ============================================================================
  // DOM Manipulation - Pickup Availability Badge
  // ============================================================================

  // Store original badge states for restoration
  var originalBadgeStates = {
    inventoryInfo: null,
    swatchBadge: null
  };

  /**
   * Update inventory badge to show "现场可取" for pickup available items
   * Updates both the inventory info section and the active swatch badge
   *
   * @param {number} productId - Shopify product ID (for PDP context)
   * @param {boolean} pickupAvailable - Whether pickup is available
   */
  function updatePickupBadge(productId, pickupAvailable) {
    // Update active swatch badge (.swatch-element.active .badge-swatch)
    updateSwatchBadge(pickupAvailable);

    log('Updated pickup badge for product ' + productId + ', pickup_available=' + pickupAvailable);
  }

  function hideInventoryInfoForEvent() {
    var selectors = config.pickupSelectors.productDetail;
    var container = document.querySelector(selectors.inventoryContainer);
    if (container) {
      container.style.display = 'none';
    }
  }

  function updateSwatchBadge(pickupAvailable) {
    // Find currently active swatch
    var activeSwatch = document.querySelector('.swatch-element.active');
    if (!activeSwatch) return;

    var badgeEl = activeSwatch.querySelector('.badge-swatch');
    if (!badgeEl) return;

    // Save original state on first call (per swatch value)
    var swatchValue = activeSwatch.getAttribute('data-value');
    if (!originalBadgeStates.swatchBadge) {
      originalBadgeStates.swatchBadge = {};
    }
    if (!originalBadgeStates.swatchBadge[swatchValue]) {
      originalBadgeStates.swatchBadge[swatchValue] = {
        text: badgeEl.textContent,
        className: badgeEl.className
      };
    }

    if (!pickupAvailable) {
      // Restore original
      var original = originalBadgeStates.swatchBadge[swatchValue];
      if (original) {
        badgeEl.textContent = original.text;
        badgeEl.className = original.className;
      }
      return;
    }

    // Update to pickup badge
    badgeEl.textContent = config.text.pickupBadgeText;
    badgeEl.className = 'badge-swatch badge-event-swatch';
  }

  /**
   * Apply badge styles from config
   *
   * @param {HTMLElement} badge - Badge element
   * @param {string} type - Badge type: 'pickup' or 'domestic'
   */
  function applyBadgeStyles(badge, type) {
    var badgeConfig = config.badges[type];
    var commonStyles = config.badges.common;

    if (!badgeConfig) return;

    badge.textContent = badgeConfig.text;
    badge.style.display = 'block';
    badge.style.background = badgeConfig.background;
    badge.style.color = badgeConfig.color;
    badge.style.padding = commonStyles.padding;
    badge.style.fontSize = commonStyles.fontSize;
    badge.style.fontWeight = commonStyles.fontWeight;
    badge.style.borderRadius = commonStyles.borderRadius;
    badge.style.position = commonStyles.position;
    badge.style.bottom = commonStyles.bottom;
    badge.style.right = commonStyles.right;
    badge.style.zIndex = commonStyles.zIndex;

    // Update classes
    badge.classList.add(badgeConfig.className);
    badge.classList.remove('badge-domestic-swatch', 'badge-oversea-swatch', 'badge-backorder-swatch', 'badge-event-swatch');
    badge.classList.add(badgeConfig.className);
  }

  /**
   * Update pickup badge in product grid/loop
   *
   * @param {HTMLElement} productElement - Product card element
   * @param {boolean} pickupAvailable - Whether pickup is available
   */
  function updateProductGridPickupBadge(productElement, pickupAvailable) {
    if (!pickupAvailable) return;

    var selectors = config.pickupSelectors.productLoop;
    var badge = productElement.querySelector(selectors.inventoryBadge);

    if (badge) {
      applyBadgeStyles(badge, 'pickup');
    }
  }

  // ============================================================================
  // Public API
  // ============================================================================

  window.EventPricing = {
    // Cookie helpers
    getCookie: getCookie,
    setCookie: setCookie,
    getEventToken: getEventToken,
    getEventSlug: getEventSlug,
    hasEventToken: hasEventToken,

    // API calls
    validateToken: validateToken,
    getItems: getItems,

    // Price formatting
    formatPrice: formatPrice,

    // Update functions
    updateProductDetailPrice: updateProductDetailPrice,
    updateProductGridPrices: updateProductGridPrices,
    updateCartPrices: updateCartPrices,

    // Pickup badge functions
    updatePickupBadge: updatePickupBadge,
    updateProductGridPickupBadge: updateProductGridPickupBadge,
    applyBadgeStyles: applyBadgeStyles
  };

  // ============================================================================
  // Auto-initialization
  // ============================================================================

  if (typeof jQuery !== 'undefined') {
    jQuery(document).ready(function() {
      // Skip initialization if disabled by page
      if (window.EventPricingDisabled) {
        log('Event pricing disabled by page');
        return;
      }

      // SAFEGUARD: Early exit if no event token
      if (!hasEventToken()) {
        log('No event token found - system inactive (normal for non-event pages)');
        return;
      }

      log('Event token detected - initializing event pricing');

      // Update product detail page if present
      // Look for the product price container which has data attributes
      var priceContainer = document.querySelector('.product-price-container[data-product-id][data-variant-id]');
      if (priceContainer) {
        var productId = parseInt(priceContainer.getAttribute('data-product-id'));
        var variantId = parseInt(priceContainer.getAttribute('data-variant-id'));
        var originalPriceCents = parseFloat(priceContainer.getAttribute('data-original-price'));
        var sku = priceContainer.getAttribute('data-sku') || '';

        // Shopify Liquid returns price in cents, convert to yuan
        var originalPrice = originalPriceCents / 100;

        // Try to get product JSON for variant data
        var productJson = null;
        var productJsonEl = document.querySelector('script[id^="ProductJson-"]');
        if (productJsonEl) {
          try {
            productJson = JSON.parse(productJsonEl.textContent);
          } catch (e) {
            logWarn('Failed to parse product JSON', e);
          }
        }

        // Get SKU from product JSON if not in data attribute
        if (!sku && productJson && productJson.variants) {
          var currentVariant = productJson.variants.find(function(v) { return v.id === variantId; });
          if (currentVariant) {
            sku = currentVariant.sku || '';
          }
        }

        if (productId && variantId && originalPrice) {
          log('PDP detected - product ' + productId + ' with ' + (productJson ? productJson.variants.length : 1) + ' variants');

          // Prefetch all variant prices first, then update current variant
          prefetchAllVariantPrices(productJson).then(function() {
            // Get event name inside callback to ensure window.EventData is populated
            var eventName = (window.EventData && window.EventData.name) ? window.EventData.name : null;
            updateProductDetailPrice(variantId, productId, originalPrice, eventName, sku);
          });
        }

        // Listen for variant changes on PDP
        // Theme triggers change on option selectors (product-select-{sectionId}-option-{index})
        var optionSelectors = document.querySelectorAll('select[id*="-option-"]');

        if (optionSelectors.length > 0 && productJson && productJson.variants) {
          // Find variant by matching option values
          var findVariantByOptions = function() {
            var selectedOptions = [];
            optionSelectors.forEach(function(selector) {
              selectedOptions.push(selector.value);
            });

            // Find variant that matches all selected options
            return productJson.variants.find(function(v) {
              for (var i = 0; i < selectedOptions.length; i++) {
                if (v['option' + (i + 1)] !== selectedOptions[i]) {
                  return false;
                }
              }
              return true;
            });
          };

          var handleVariantChange = function() {
            var variant = findVariantByOptions();
            if (!variant) {
              resetProductDetailPrice(productId);
              return;
            }

            var newVariantId = variant.id;
            var newPrice = variant.price / 100;
            var newSku = variant.sku || '';

            // Update compare_at_price attribute for crossed-out price
            if (priceContainer) {
              priceContainer.setAttribute('data-compare-at-price', variant.compare_at_price || 0);
            }

            log('Variant changed to ' + newVariantId + ', price: ' + newPrice);
            var eventName = (window.EventData && window.EventData.name) ? window.EventData.name : null;
            updateProductDetailPrice(newVariantId, productId, newPrice, eventName, newSku);
          };

          // Listen to all option selectors
          optionSelectors.forEach(function(selector) {
            selector.addEventListener('change', handleVariantChange);
          });

          log('Listening to ' + optionSelectors.length + ' option selectors for variant changes');
        }

      }

      // Update product grid if present (skip if event collection tabs will handle it)
      var hasEventCollectionTabs = document.querySelector('[id^="event-collection-tabs"]');
      if (!hasEventCollectionTabs && document.querySelector(config.selectors.productLoop.productCard)) {
        updateProductGridPrices();
      }

      // Update cart if present
      if (document.getElementById('cart-table') || document.getElementById('cart-dropdown')) {
        updateCartPrices();
      }

      // Listen for cart updates
      jQuery(document).on('cart:updated', function() {
        if (hasEventToken()) {
          logVerbose('cart:updated event detected');
          updateCartPrices();
        }
      });

    });
  }

  // Register event listeners immediately (outside jQuery ready) to avoid timing issues
  // These may fire before jQuery ready on some devices
  window.addEventListener('productsRendered', function(e) {
    if (hasEventToken()) {
      logVerbose('productsRendered event detected:', e.detail);
      updateProductGridPrices();
    }
  });

  window.addEventListener('eventProductsLoaded', function(e) {
    if (hasEventToken()) {
      logVerbose('eventProductsLoaded event detected:', e.detail);
      updateProductGridPrices();
    }
  });

  window.addEventListener('pickupProductsLoaded', function(e) {
    if (hasEventToken()) {
      logVerbose('pickupProductsLoaded event detected:', e.detail);
      updateProductGridPrices();
    }
  });

  log('Event pricing system loaded');

  // Dispatch ready event for other scripts
  if (typeof window.CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('eventPricingReady'));
  }

})();
